<!-- In order to share props from child component to parent component -->
<svelte:options accessors={true} />

<script>
    import { onMount } from "svelte";

    import { apiMethods } from "../../Services/index.svelte";

    import { cloneDeep } from "lodash";

    import {
        downloadJson,
        readJsonFromFileInput,
        getNumberWithComma,
        getText,
        isObjectEmpty,
        showError,
        showInfo,
    } from "../../Utils/utilFunctions.svelte";

    import {
        CardLayout,
        CreatePresetModal,
        LoadingModal,
        NotificationModal,
        PageLoading,
        Pagination,
        SvelteTable,
    } from "../../Components/Common/index.svelte";

    import { isMobile, user } from "../../stores";

    import { DirectUserRoles, pageOptions } from "../../Utils/utilEnums.svelte";

    import {
        addIcon,
        deleteIcon,
        duplicateIcon,
        editIcon,
        exportIcon,
        uploadIcon,
    } from "../../Utils/utilConstants.svelte";

    import { getDoctorInsuranceTypesForImport } from "./helpers/presetsHelper.svelte";

    export let params = {};
    export let inCardMode = false;
    export let cardModeConfig = {};
    export let isLoadingDoctorDetails = false;
    export let doctorDetails = {};
    export let reloadBillingRules = () => {};

    $: isUserDoctor = $user.role == DirectUserRoles.Doctor;

    let isLoadingPresets = false,
        returnedPresets = [];

    let isModifyPresetModalOpen = false,
        isDeletePresetModalOpen = false,
        isModifyingPreset = false,
        selectedPreset = {};

    let page = 0,
        pageSize = inCardMode ? 999999 : 25,
        fileInput,
        count;

    let isImporting = false;

    const columns = [
        {
            key: "name",
            title: "Preset name",
            headerClass: "text-left pl-4 w-1/4 rounded-tl-xl ",
            class: "text-left pl-4 cursor-default",
            renderValue: (v) => getText(v.name),
        },
        {
            key: "itemType",
            title: "Items type",
            headerClass: "text-left w-1/4 pl-3",
            class: "text-left cursor-default pl-3",
            renderValue: (v) => `${v.useAsaCodes ? "ASA" : "MBS"}`,
        },
        {
            key: "items",
            title: "Items",
            headerClass: "text-left w-1/2 pl-3",
            class: "text-left cursor-default pl-3",
            renderValue: (v) => _renderItems(v),
        },
        {
            key: "actions",
            title: "Actions",
            headerClass: "text-left w-[2%] rounded-tr-xl px-3",
            class: "cursor-default px-3",
            renderValue: (v) => _renderActionIcons(),
        },
    ];

    onMount(async () => {
        // params.id is AccountId if user role is other than doctor
        // params.id is UserId if user role is doctor, need further api call to get its associated accountId

        !isUserDoctor &&
            params &&
            params.id &&
            (await loadPresets(page * pageSize, pageSize, _getSearchParams()));

        isUserDoctor && (await loadDoctorDetails());
    });

    const loadPresets = async (start, limit, params) => {
        isLoadingPresets = true;

        await apiMethods.getMethod
            .getAccountPresets(start, limit, params)
            .then((res) => _handleReturnedPresets(res))
            .catch((err) => showError(err));

        isLoadingPresets = false;
    };

    const loadDoctorDetails = async () => {
        isLoadingDoctorDetails = true;

        await apiMethods.getMethod
            .getAccountByUserId(Number(params.id))
            .then(async (res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            "Failed to load doctor details, please refresh the page and try again and contact support if the issue persists.",
                    );
                else {
                    doctorDetails = { ...res };
                    params.id = res.id;
                    await loadPresets(
                        page * pageSize,
                        pageSize,
                        _getSearchParams(),
                    );
                }
            });

        isLoadingDoctorDetails = false;
    };

    const handleDeletePreset = async () => {
        isModifyingPreset = true;

        await apiMethods.deleteMethod
            .deleteAccountPreset(selectedPreset.id)
            .then(async (res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            "Failed to delete selected preset, please refresh the page and try again and contact support if the issue persists.",
                    );
                else await handleAfterModifyPreset();
            })
            .catch((err) => showError(err));

        isModifyingPreset = false;

        closeModifyPresetModal();
    };

    const handleAfterModifyPreset = async () => {
        await loadPresets(page * pageSize, pageSize, _getSearchParams());

        await apiMethods.getMethod
            .getAccountById(Number(params.id))
            .then((res) => (doctorDetails = { ...res }));
    };

    export const openModifyPresetModal = (data = {}) => {
        if (isLoadingDoctorDetails) return;

        selectedPreset = { ...data };
        isModifyPresetModalOpen = true;
    };

    export const openCopyPresetModal = (data = {}) => {
        if (isLoadingDoctorDetails) return;

        selectedPreset = { ...data, id: null, fromId: data.id };
        isModifyPresetModalOpen = true;
    };

    const openDeletePresetModal = (data = {}) => {
        if (!data || isObjectEmpty(data)) return;

        selectedPreset = { ...data };
        isDeletePresetModalOpen = true;
    };

    const closeModifyPresetModal = () => {
        isModifyPresetModalOpen = false;
        isDeletePresetModalOpen = false;
        selectedPreset = {};
        isModifyingPreset = false;
    };

    const handleClickCell = ({ target: { className } }, rowData, key) => {
        if (key == "actions") {
            if (className.includes("editIcon")) openModifyPresetModal(rowData);
            if (className.includes("duplicateIcon")) openCopyPresetModal(rowData);
            else if (className.includes("deleteIcon"))
                openDeletePresetModal(rowData);
        }
    };

    export const exportPresets = () => {
        if (!returnedPresets.length) return showInfo("No Presets Available");
        const { providers, billingRules } = doctorDetails;
        const copyData = cloneDeep(returnedPresets);
        delete copyData["$expanded"];
        delete copyData["$selected"];
        delete copyData["$sortOn"];
        copyData.forEach((preset) => {
            preset.billingRules?.forEach((billingRule) => {
                const relatedProviderNumber = providers.find(
                    (provider) => provider.id === billingRule.doctorProviderId,
                );
                if (relatedProviderNumber)
                    billingRule.doctorProviderNumber =
                        relatedProviderNumber.number;

                const relatedBillingRule = billingRules.find(
                    (b) => b.id === billingRule.billingRuleId,
                );
                if (relatedBillingRule)
                    billingRule.billingRuleName = relatedBillingRule.name;
            });
        });
        downloadJson(copyData, "Presets");
    };

    export const startUploadPreset = () => fileInput?.click();

    const onFileChange = (e) => {
        const file = e.target.files[0];

        const presetKeys = [
            "useAsaCodes",
            "name",
            "mbsCodes",
            "asaCodes",
            "billingRules",
        ];

        let reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = async (e) => {
            const fileData = readJsonFromFileInput(e);
            if (!Array.isArray(fileData)) return;
            const promises = [];
            const { providers, billingRules } = doctorDetails;

            fileData.forEach(async (preset) => {
                const importedPreset = {};
                presetKeys.forEach((key) => {
                    if (preset[key] !== undefined)
                        importedPreset[key] = preset[key];
                });
                // return if no valid name or billing rules
                if (
                    !importedPreset.name ||
                    !importedPreset.billingRules?.length
                )
                    return showInfo(
                        `Imported preset named: ${importedPreset.name ?? ""} is invalid, please check data or add manually;`,
                    );
                if (importedPreset.useAsaCodes === undefined)
                    importedPreset.useAsaCodes = false;
                importedPreset.accountId = doctorDetails.id;

                const doctorInsuranceTypes = getDoctorInsuranceTypesForImport(
                    doctorDetails,
                    importedPreset.useAsaCodes,
                );

                doctorInsuranceTypes.forEach((insuranceType) => {
                    if (insuranceType.isHidden) return;
                    const matchedBillingRule = importedPreset.billingRules.find(
                        (rule) =>
                            rule.presetRuleType ===
                            insuranceType.presetRuleType,
                    );

                    if (matchedBillingRule) {
                        // filter available provider number and match, no.6 for NonPreferedADF
                        const availableProviders = providers.filter(
                            (provider) =>
                                insuranceType.presetRuleType !== 6 ||
                                !provider.isPreferredDefence,
                        );
                        if (availableProviders.length) {
                            const defaultProvider = availableProviders.find(
                                (p) => p.isDefault,
                            );
                            const matchedProvider = availableProviders.find(
                                (ap) =>
                                    ap.number ===
                                    matchedBillingRule.doctorProviderNumber,
                            );
                            insuranceType.doctorProviderId =
                                matchedProvider?.id ?? defaultProvider?.id ?? 0;
                        }

                        // filter available billing rule then match name
                        // asa use out of pocket only. mbs has all
                        const availableBillingTypes = billingRules.filter(
                            (rule) =>
                                (importedPreset.useAsaCodes &&
                                    rule.type === 2) ||
                                !importedPreset.useAsaCodes,
                        );
                        // case: always ask, hard coded
                        if (matchedBillingRule.billingRuleId === -1)
                            insuranceType.billingRuleId = -1;
                        else if (availableBillingTypes.length) {
                            const defaultBillingType =
                                availableBillingTypes.find((b) => b.isDefault);
                            const matchedBillingType =
                                availableBillingTypes.find(
                                    (bt) =>
                                        bt.name ===
                                        matchedBillingRule.billingRuleName,
                                );
                            insuranceType.billingRuleId =
                                matchedBillingType?.id ??
                                defaultBillingType?.id ??
                                0;
                        }

                        if (insuranceType.showCapCheckbox) {
                            insuranceType.maxGapProtection =
                                matchedBillingRule.maxGapProtection;
                        }
                    }
                });

                importedPreset.billingRules = doctorInsuranceTypes;
                if (importedPreset.useAsaCodes) {
                    importedPreset.asaCodes?.forEach((code) => {
                        code.accountId = doctorDetails.id;
                        delete code["accountPresetId"];
                    });
                } else {
                    importedPreset.mbsCodes?.forEach((code) => {
                        code.accountId = doctorDetails.id;
                        delete code["accountPresetId"];
                    });
                }

                promises.push(
                    apiMethods.postMethod
                        .createAccountPresets(importedPreset)
                        .then((res) => {
                            if (!res || res.error)
                                showError(
                                    res.error ??
                                        "Failed to create preset, please refresh the page and try again and contact support if the issue persists.",
                                );
                        })
                        .catch((err) => showError(err)),
                );
            });

            fileInput.value = null;

            if (!promises.length) return;

            isImporting = true;
            await Promise.all(promises);
            isImporting = false;
            handleAfterModifyPreset();
        };
    };

    const onPageSizeChange = (newPageSize) => {
        pageSize = newPageSize;
        page = 0;

        loadPresets(0, newPageSize, _getSearchParams());
    };

    const onPageChange = (e) => {
        page = Number(e.detail.page);

        loadPresets(
            Number(e.detail.firstDataIndex),
            pageSize,
            _getSearchParams(),
        );
    };

    // HELPER FUNCTIONS
    const _handleReturnedPresets = (res) => {
        if (!res || res.error)
            showError(
                res.error ??
                    "Failed to load doctor presets, please refresh the page and try again and contact support if the issue persists.",
            );
        else {
            returnedPresets = [...res.results];
            count = res.total;
        }
    };

    const _getSearchParams = () => {
        let para = { "filter[doctorId]": Number(params.id) };

        return para;
    };

    const _renderItems = (data) => {
        const { useAsaCodes, asaCodes, mbsCodes, procedureLength } = data;

        const res = [];

        if (useAsaCodes)
            asaCodes.map(({ itemCode, subItemCode }) => {
                const arr = `${itemCode} ${subItemCode == "" || subItemCode == "0" ? "" : `- ${subItemCode}`}`;

                res.push(arr);
            });

            if(procedureLength && procedureLength > 0) {
                res.push(`Time (${procedureLength} minutes)`);
            }
        else
            mbsCodes.map(({ itemNum, subItemNum }) => {
                const arr = `${itemNum} ${subItemNum == "" || subItemNum == 0 ? "" : `- ${subItemNum}`}`;

                res.push(arr);
            });

        return res.join(", ");
    };

    const _renderActionIcons = () => {
        return `<div class="flex flex-1 items-center justify-start gap-x-2">
                    <div class="duplicateIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center w-[30px] h-[30px] cursor-pointer hover:rounded-full hover:bg-secondary hover:text-white" data-tip="Duplicate Preset">
                        <i class="duplicateIcon cursor-pointer ${duplicateIcon}"></i>
                    </div>

                    <div class="editIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center w-[30px] h-[30px] cursor-pointer hover:rounded-full hover:bg-secondary hover:text-white" data-tip="Edit Preset">
                        <i class="editIcon cursor-pointer ${editIcon}"></i>
                    </div>

                    <div class="deleteIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center w-[30px] h-[30px] cursor-pointer hover:rounded-full hover:bg-secondary hover:text-white" data-tip="Delete Preset">
                        <i class="deleteIcon cursor-pointer ${deleteIcon}"></i>
                    </div>
                </div>`;
    };
</script>

{#if inCardMode}
    <CardLayout
        title={cardModeConfig.title ?? "Presets"}
        btnOptions={cardModeConfig.btnOptions ?? []}
    >
        {#if isLoadingPresets}
            <div class="my-6">
                <PageLoading />
            </div>
        {:else}
            <div class="flex flex-1 overflow-auto">
                <SvelteTable
                    {columns}
                    rows={returnedPresets}
                    clickCellFunc={({ detail: { event, row, key } }) => {
                        handleClickCell(event, row, key);
                    }}
                />
            </div>
        {/if}
    </CardLayout>
{:else}
    <div class="relative flex-1">
        <div class="flex flex-col gap-y-4 absolute inset-0">
            <div class="flex w-full items-center justify-end gap-x-3">
                {#if returnedPresets?.length}
                    <button
                        class="btn btn-sm btn-secondary flex items-center gap-x-2"
                        on:click|preventDefault={exportPresets}
                    >
                        <i class={`${exportIcon}`} />
                        <p>Export Presets</p>
                    </button>
                {/if}

                <button
                    class="btn btn-sm btn-secondary flex items-center gap-x-2"
                    on:click|preventDefault={startUploadPreset}
                >
                    <i class={`${uploadIcon}`} />
                    <p>Import Presets</p>
                </button>

                <button
                    class={`btn btn-sm btn-secondary flex items-center gap-x-2 ${isLoadingDoctorDetails ? "loading" : ""}`}
                    on:click|preventDefault={() => openModifyPresetModal()}
                    disabled={isLoadingDoctorDetails}
                >
                    {#if isLoadingDoctorDetails}
                        <p>Preparing Data</p>
                    {:else}
                        <i class={`${addIcon}`} />
                        <p>Create Preset</p>
                    {/if}
                </button>
            </div>

            <div class={$isMobile ? "h-auto w-minus48" : "relative flex-1"}>
                {#if isLoadingPresets}
                    <PageLoading />
                {:else}
                    <div
                        class={$isMobile
                            ? "overflow-x-scroll overflow-y-visible"
                            : "overflow-y-auto absolute inset-0"}
                    >
                        <SvelteTable
                            {columns}
                            rows={returnedPresets}
                            clickCellFunc={({
                                detail: { event, row, key },
                            }) => {
                                handleClickCell(event, row, key);
                            }}
                        />
                    </div>
                {/if}
            </div>

            <div class="flex flex-col">
                <div class="flex flex-1 justify-between items-center">
                    <div class="flex flex-row items-center gap-x-4">
                        <div
                            class="flex items-center {$isMobile
                                ? 'flex-col'
                                : 'flex-row'}"
                        >
                            <strong class="text-gray-600 text-sm"
                                >Table size:</strong
                            >
                            <select
                                class="select select-sm text-gray-600 m-2"
                                bind:value={pageSize}
                                on:change|preventDefault={() =>
                                    onPageSizeChange(pageSize)}
                            >
                                {#each pageOptions as pageOption}
                                    <option
                                        value={pageOption.amount}
                                        selected={pageOption.amount ===
                                            pageSize}
                                    >
                                        {pageOption.amount}
                                    </option>
                                {/each}
                            </select>
                        </div>

                        <strong class="text-gray-600 text-sm">
                            Total records:
                            <span class="text-gray-600"
                                >{getNumberWithComma(count)}</span
                            >
                        </strong>
                    </div>

                    {#if returnedPresets.length}
                        <Pagination
                            {page}
                            {pageSize}
                            {count}
                            on:onPageChange={onPageChange}
                        />
                    {/if}
                </div>
            </div>
        </div>
    </div>
{/if}

<input
    type="file"
    accept=".json"
    style="display: none"
    id="fileInput"
    on:change={onFileChange}
    bind:this={fileInput}
/>

{#if isModifyPresetModalOpen}
    <CreatePresetModal
        closeModal={closeModifyPresetModal}
        onSubmit={handleAfterModifyPreset}
        {selectedPreset}
        {doctorDetails}
        {reloadBillingRules}
    />
{/if}

<NotificationModal
    isModalOpen={isDeletePresetModalOpen}
    title="Delete selected preset?"
    isReverseBtns
    cancelText="Delete"
    submitText="Cancel"
    confirmButtonClass="btn-accent"
    closeModal={handleDeletePreset}
    confirmAction={closeModifyPresetModal}
    isConfirming={isModifyingPreset}
    closeModalFunction={closeModifyPresetModal}
>
    <div class="flex flex-col leading-6 gap-y-4">
        <div class="flex flex-1 gap-x-4">
            <p class="title">Preset name:</p>
            <p class="flex-1">{getText(selectedPreset.name)}</p>
        </div>

        <div class="flex flex-1 gap-x-4">
            <p class="title">Items type:</p>
            <p class="flex-1">
                {getText(selectedPreset.useAsaCodes ? "ASA" : "MBS")}
            </p>
        </div>

        <div class="flex flex-1 gap-x-4">
            <p class="title">Items:</p>
            <p class="flex-1">
                {_renderItems(selectedPreset)}
            </p>
        </div>
    </div>
</NotificationModal>

{#if isImporting}
    <LoadingModal titleText="Importing Presets" />
{/if}

<style>
    .title {
        width: 120px;
    }
</style>
